import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-secondary/40 border-r border-border">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 grid place-items-center rounded-md bg-primary text-primary-foreground"><Zap className="w-5 h-5" /></div>
          <span className="font-display text-xl font-semibold tracking-tight">DIGI-JIRA</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-semibold tracking-tight leading-tight">
            The workspace where engineering teams actually ship.
          </h1>
          <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
            Issues, sprints, roadmaps and reports — one keyboard-driven surface. Built for teams that live in the tools they use.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-3">
            {["Kanban / Scrum","Sprints","Backlog","Roadmaps","Reports","Command palette"].map(x => (
              <div key={x} className="border border-border rounded-md px-3 py-2 text-xs text-muted-foreground bg-background">{x}</div>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">© DIGI-JIRA Systems</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 grid place-items-center rounded-md bg-primary text-primary-foreground"><Zap className="w-4 h-4" /></div>
            <span className="font-display text-lg font-semibold">DIGI-JIRA</span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your work email to continue.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full active:scale-[0.98] transition-transform" data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
          <div className="text-sm text-muted-foreground text-center">
            No account? <Link to="/register" className="text-primary hover:underline" data-testid="link-register">Create one</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
